import { useCallback, useEffect, useRef } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import {
  TERMINAL_INDEX_STATUSES,
  refreshFilesInCache,
} from '@/hooks/files/cache/fileCacheSync'
import {
  organizationFilesEntityExtractionStatusQueryKeys,
  organizationFilesIndexStatusQueryKeys,
  organizationFilesQueryKeys,
} from '@/hooks/organization/types'

import type { PaginatedResponse } from '@/types/Api'
import type { File } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { LiveBatchStatusResponse } from '@/types/Status'

const NOT_FOUND_GRACE_MS = 20_000
const EXTRACTION_POLL_MS = 2_000
const EXTRACTION_STARTUP_GRACE_MS = 20_000
const LIVE_POLL_INTERVAL_MS = 5_000
const IDLE_POLL_INTERVAL_MS = 30 * 60 * 1000

interface UseOrganizationFilesIndexStatusArgs<T> {
  orgId: UUID | ''
  select?: (data: LiveBatchStatusResponse) => T
}

export const fetchOrganizationFilesIndexStatus = async (
  orgId: string,
  accessToken: string | undefined | null,
): Promise<LiveBatchStatusResponse> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }
  const res: Response = await fetch(
    `${API_URL}/api/v1/jobs/live/index-organization-files/key/${orgId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({}),
    },
  )
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return (await res.json()) as LiveBatchStatusResponse
}

export const boostOrganizationFilesIndexPolling = ({
  queryClient,
  orgId,
  durationMs = NOT_FOUND_GRACE_MS,
}: {
  queryClient: QueryClient
  orgId: UUID | ''
  durationMs?: number
}) => {
  if (!orgId) {
    return
  }

  queryClient.setQueryData(
    organizationFilesIndexStatusQueryKeys.pollBoost({ orgId }),
    Date.now() + durationMs,
  )
  void queryClient.refetchQueries({
    queryKey: organizationFilesIndexStatusQueryKeys.live({ orgId }),
    type: 'active',
  })
}

const getRefetchInterval = ({
  data,
  graceStartedAt,
  hasActiveFileInCache,
  pollBoostUntil,
}: {
  data: LiveBatchStatusResponse | undefined
  graceStartedAt: number | null
  hasActiveFileInCache: boolean
  pollBoostUntil: number | null
}) => {
  if (pollBoostUntil !== null && Date.now() < pollBoostUntil) {
    return LIVE_POLL_INTERVAL_MS
  }
  if (hasActiveFileInCache) {
    return LIVE_POLL_INTERVAL_MS
  }
  if (!data) {
    return LIVE_POLL_INTERVAL_MS
  }
  if (data.is_running) {
    return LIVE_POLL_INTERVAL_MS
  }
  if (
    Object.keys(data.status).length === 0 &&
    graceStartedAt !== null &&
    Date.now() - graceStartedAt < NOT_FOUND_GRACE_MS
  ) {
    return LIVE_POLL_INTERVAL_MS
  }
  return IDLE_POLL_INTERVAL_MS
}

export const useOrganizationFilesIndexStatus = <T = LiveBatchStatusResponse>({
  orgId,
  select,
}: UseOrganizationFilesIndexStatusArgs<T>) => {
  const user = useAuthInfo()
  const queryClient = useQueryClient()
  const graceStartedAtRef = useRef<number | null>(null)
  const previousOrgIdRef = useRef<UUID | ''>(orgId)
  const selectFn = useCallback(
    (data: LiveBatchStatusResponse) =>
      select ? select(data) : (data as unknown as T),
    [select],
  )

  if (previousOrgIdRef.current !== orgId) {
    previousOrgIdRef.current = orgId
    graceStartedAtRef.current = null
  }

  const hasActiveFileInCache = useCallback(() => {
    if (!orgId) {
      return false
    }

    const files =
      queryClient.getQueryData<PaginatedResponse<Pick<File, 'index_status'>>>(
        organizationFilesQueryKeys.organization({ orgId }),
      )?.data ?? []

    return files.some((file) => !TERMINAL_INDEX_STATUSES.has(file.index_status))
  }, [orgId, queryClient])

  return useQuery({
    queryFn: () => fetchOrganizationFilesIndexStatus(orgId, user?.accessToken),
    queryKey: organizationFilesIndexStatusQueryKeys.live({ orgId }),
    refetchOnWindowFocus: true,
    enabled: orgId !== '',
    staleTime: Infinity,
    refetchInterval: (query) => {
      const data = query.state.data
      const pollBoostUntil =
        queryClient.getQueryData<number>(
          organizationFilesIndexStatusQueryKeys.pollBoost({ orgId }),
        ) ?? null
      const hasActiveFiles = hasActiveFileInCache()
      if (!data) {
        graceStartedAtRef.current = null
        return getRefetchInterval({
          data,
          graceStartedAt: graceStartedAtRef.current,
          hasActiveFileInCache: hasActiveFiles,
          pollBoostUntil,
        })
      }

      if (data.is_running || Object.keys(data.status).length > 0) {
        graceStartedAtRef.current = null
        queryClient.removeQueries({
          queryKey: organizationFilesIndexStatusQueryKeys.pollBoost({ orgId }),
          exact: true,
        })
      } else if (graceStartedAtRef.current === null) {
        graceStartedAtRef.current = Date.now()
      }

      return getRefetchInterval({
        data,
        graceStartedAt: graceStartedAtRef.current,
        hasActiveFileInCache: hasActiveFiles,
        pollBoostUntil,
      })
    },
    select: selectFn,
  })
}

interface UseInvalidateOrganizationFilesOnSuccessArgs {
  orgId: UUID | ''
}

export const useInvalidateOrganizationFilesOnNotRunning = ({
  orgId,
}: UseInvalidateOrganizationFilesOnSuccessArgs) => {
  const queryClient = useQueryClient()
  const prevActiveIdsRef = useRef<UUID[]>([])
  const lastIndexingActiveAtRef = useRef<number | null>(null)
  const previousOrgIdRef = useRef<UUID | ''>(orgId)
  const user = useAuthInfo()
  const accessToken = user?.accessToken

  const { data } = useOrganizationFilesIndexStatus({ orgId })

  if (previousOrgIdRef.current !== orgId) {
    previousOrgIdRef.current = orgId
    lastIndexingActiveAtRef.current = null
  }

  useEffect(() => {
    const currentActiveIds = Object.keys(data?.status ?? {}) as UUID[]
    const previousActiveIds = prevActiveIdsRef.current
    const completedIds = previousActiveIds.filter(
      (fileId) => !currentActiveIds.includes(fileId),
    )
    prevActiveIdsRef.current = currentActiveIds

    if (!accessToken || completedIds.length === 0) {
      return
    }

    void refreshFilesInCache({
      queryClient,
      accessToken,
      fileIds: completedIds,
      orgId,
    })
  }, [accessToken, data?.status, orgId, queryClient])

  useEffect(() => {
    if (data?.is_running) {
      lastIndexingActiveAtRef.current = Date.now()
    }

    const withinExtractionStartupGrace =
      lastIndexingActiveAtRef.current !== null &&
      Date.now() - lastIndexingActiveAtRef.current < EXTRACTION_STARTUP_GRACE_MS

    if (!data?.is_running && !withinExtractionStartupGrace) {
      return
    }

    const extractionQueryKey =
      organizationFilesEntityExtractionStatusQueryKeys.live({ orgId })
    const interval = setInterval(() => {
      if (
        !data?.is_running &&
        lastIndexingActiveAtRef.current !== null &&
        Date.now() - lastIndexingActiveAtRef.current >=
          EXTRACTION_STARTUP_GRACE_MS
      ) {
        clearInterval(interval)
        return
      }

      queryClient.invalidateQueries({ queryKey: extractionQueryKey })
    }, EXTRACTION_POLL_MS)

    return () => clearInterval(interval)
  }, [data?.is_running, orgId, queryClient])
}
